import { ActionIcon, Drawer } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconArrowBarLeft,
  IconArrowBarRight,
  IconSparkles
} from "@tabler/icons-react";
import { useRef } from "react";
import { useShouldShowStaffView } from "../StaffViewToggle/useShouldShowStaffView";
import "./AiChat.scss";
import { ChatPanel } from "./ChatPanel/ChatPanel";

const DRAWER_SIZE_NORMAL = "lg";
const DRAWER_SIZE_EXPANDED = "50vw";

// Matches the Userpilot launcher, a 36px circle sitting 7px from the right edge
// and 22px above the bottom.
const ICON_SIZE = 36;
const BOTTOM_OFFSET = 22;
const RIGHT_OFFSET = 24;
// 7px launcher offset + 36px launcher + 12px gap.
const RIGHT_OFFSET_BESIDE_USERPILOT = 55;

interface Props {
  userpilotEnabled?: boolean;
}

function AiChat({ userpilotEnabled = false }: Props) {
  const [opened, { open, close }] = useDisclosure(false);
  const [expanded, { toggle: toggleExpanded }] = useDisclosure(false);
  const { shouldShowStaffView } = useShouldShowStaffView();
  const inputRef = useRef<HTMLInputElement>(null);

  if (!shouldShowStaffView) {
    return null;
  }

  return (
    <>
      <ActionIcon
        aria-label="opti.Agent öffnen"
        bottom={BOTTOM_OFFSET}
        className="AiChatTrigger"
        color="accent1.7"
        pos="fixed"
        right={userpilotEnabled ? RIGHT_OFFSET_BESIDE_USERPILOT : RIGHT_OFFSET}
        size={ICON_SIZE}
        onClick={open}
      >
        <IconSparkles size={20} />
      </ActionIcon>
      <Drawer
        className="AiChatDrawer"
        closeOnEscape={false}
        keepMounted
        lockScroll={false}
        opened={opened}
        position="right"
        size={expanded ? DRAWER_SIZE_EXPANDED : DRAWER_SIZE_NORMAL}
        styles={{
          body: {
            height: "calc(100% - 60px)",
            padding: 0,
            position: "relative"
          },
          content: { minWidth: 0, overflow: "visible" },
          header: {
            borderBottom: "1px solid var(--mantine-color-default-border)"
          },
          inner: { overflow: "visible" },
          title: { fontSize: "var(--mantine-font-size-md)", fontWeight: 600 }
        }}
        title="opti.Agent"
        transitionProps={{ onEntered: () => inputRef.current?.focus() }}
        withOverlay={false}
        onClose={close}
      >
        <ActionIcon
          className="AiChatExpandToggle"
          left={0}
          pos="absolute"
          radius="xl"
          size="md"
          top="50%"
          variant="default"
          onClick={toggleExpanded}
        >
          {expanded ? (
            <IconArrowBarRight size={16} />
          ) : (
            <IconArrowBarLeft size={16} />
          )}
        </ActionIcon>
        <ChatPanel inputRef={inputRef} userpilotEnabled={userpilotEnabled} />
      </Drawer>
    </>
  );
}

export { AiChat };
